import { useEffect } from "react";
import { isWebView } from "@b2bportal/core-utilities";

/**
 * Global scroll fix for RBC mobile app
 *
 * Fixes viewport scroll issues when mobile app keyboard appears and autocomplete modals close.
 * The native app header/footer can cause the WebView to scroll incorrectly when the keyboard
 * appears, leaving the search inputs cut off when returning from autocomplete.
 *
 * This hook listens for autocomplete blur/close events and resets scroll position.
 *
 * Performance considerations:
 * - Uses debouncing to prevent race conditions from rapid interactions
 * - Only scrolls when position has actually changed (accessibility)
 * - Uses specific selectors to minimize unnecessary checks
 */
export const useMobileAppScrollFix = () => {
  useEffect(() => {
    // Only apply scroll fix in WebView - mobile web handles scroll correctly via Radix Dialog
    if (!isWebView()) {
      return;
    }
    // Track scroll timeout to prevent race conditions when multiple
    // autocomplete interactions happen in quick succession
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const scrollToTop = () => {
      // Clear any pending scroll timeout to avoid janky behavior
      // from multiple queued scroll operations
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Use a timeout to ensure the keyboard has fully dismissed,
      // the dialog close animation has completed (200ms), and the
      // WebView has stabilized before resetting scroll position.
      // The scroll check is done inside the timeout so it captures
      // the final scroll position after keyboard dismiss.
      scrollTimeout = setTimeout(() => {
        const currentScroll =
          window.scrollY || document.documentElement.scrollTop;

        if (currentScroll === 0) {
          scrollTimeout = null;
          return;
        }

        window.scrollTo({ top: 0, behavior: "auto" });
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        scrollTimeout = null;
      }, 300);
    };

    /**
     * Checks if the target element is an autocomplete input field.
     * Excludes Radix Select triggers which also use role="combobox"
     * but are not autocomplete inputs (RBC-1283).
     */
    const isAutocompleteInput = (target: HTMLElement): boolean => {
      if (!target) return false;

      if (target.getAttribute?.("role") === "combobox") {
        // Radix Select triggers use role="combobox" with aria-autocomplete="none".
        // Real autocomplete inputs omit aria-autocomplete or set it to "list"/"both".
        if (target.getAttribute?.("aria-autocomplete") === "none") return false;
        return true;
      }

      if (target.classList?.contains?.("MuiAutocomplete-input")) {
        return true;
      }

      if (target.getAttribute?.("data-autocomplete") === "true") {
        return true;
      }

      return false;
    };

    // Listen for blur events on autocomplete inputs
    const handleBlur = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      if (isAutocompleteInput(target)) {
        scrollToTop();
      }
    };

    // Listen for click events on autocomplete options (when user selects a location).
    // Excludes Radix Select options to avoid interfering with non-autocomplete
    // dropdowns like activity time slot selector (RBC-1283).
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!target) return;

      // Skip Radix Select options — they use role="option" but are not autocomplete results
      if (target.closest?.("[data-radix-select-viewport]")) return;

      const isAutocompleteOption =
        target.classList?.contains?.("MuiAutocomplete-option") ||
        target.closest?.("[role='option']");

      if (isAutocompleteOption) {
        scrollToTop();
      }
    };

    document.addEventListener("blur", handleBlur, true);
    document.addEventListener("click", handleClick, true);

    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      document.removeEventListener("blur", handleBlur, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);
};
